import React, { useState } from 'react';
import { CompanyFormData, Sector, SectorCreatePayload, Qualification } from '../types';
import { COLORS } from '../../../../core/constants/colors';
import { CountrySelect } from '../../../../core/components/CountrySelect';
import { SectorSelect } from '../../../../core/components/SectorSelect';
import { QualificationSelect } from '../../../../core/components/QualificationSelect';
import { EmployeeRangeSelect } from '../../../../core/components/EmployeeRangeSelect';
import { AddSectorModal } from './AddSectorModal';

interface Props {
  onClose: () => void;
  onSubmit: (data: CompanyFormData) => Promise<void>;
  sectors: Sector[];
  onCreateSector: (payload: SectorCreatePayload) => Promise<Sector | null>;
  qualifications: Qualification[];
}

const INITIAL_FORM_DATA: CompanyFormData = {
  name: '',
  domain: '',
  description: '',
  website: '',
  founding_year: undefined,
  employees: '',
  industry: '',
  location: '',
  linkedin_url: '',
  auto_enrich: true,
  sector_id: null,
  qualification_id: null,
};

export const CreateCompanyDrawer: React.FC<Props> = ({ onClose, onSubmit, sectors, onCreateSector, qualifications }) => {
  const [formData, setFormData] = useState<CompanyFormData>(INITIAL_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showAddSector, setShowAddSector] = useState(false);

  const handleCreateSector = async (payload: SectorCreatePayload) => {
    const created = await onCreateSector(payload);
    if (!created) throw new Error('Failed to create sector');
    setFormData((prev) => ({ ...prev, sector_id: created.id }));
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.domain || !formData.industry || !formData.location) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      resetForm();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create company');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDiscard = () => {
    resetForm();
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Side Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-6 py-5 border-b flex items-center justify-between" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold" style={{ color: '#1F2937' }}>
              Add Company
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-all"
              style={{ color: '#6B7280' }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="flex-1 px-6 py-6 space-y-6">
            {error && (
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#FEE2E2' }}>
                <p className="text-sm" style={{ color: '#DC2626' }}>{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                Business name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB' }}
                placeholder="Enter business name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                Domain *
              </label>
              <input
                type="text"
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB' }}
                placeholder="example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                Industry *
              </label>
              <input
                type="text"
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB' }}
                placeholder="e.g., Technology & IT"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                Sector
              </label>
              <SectorSelect
                sectors={sectors}
                value={formData.sector_id ?? null}
                onChange={(value) => setFormData({ ...formData, sector_id: value })}
                onAddSector={() => setShowAddSector(true)}
                placeholder={sectors.length === 0 ? 'No sectors yet — click below to add one' : 'Select a sector...'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                Qualification
              </label>
              <QualificationSelect
                qualifications={qualifications}
                value={formData.qualification_id ?? null}
                onChange={(value) => setFormData({ ...formData, qualification_id: value })}
                placeholder={qualifications.length === 0 ? 'No qualifications available' : 'Select a qualification...'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                Location *
              </label>
              <CountrySelect
                value={formData.location}
                onChange={(value) => setFormData({ ...formData, location: value })}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                Description
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB' }}
                placeholder="Company description"
                rows={4}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                Website
              </label>
              <input
                type="text"
                value={formData.website || ''}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB' }}
                placeholder="https://example.com"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                  Founding Year
                </label>
                <input
                  type="number"
                  value={formData.founding_year || ''}
                  onChange={(e) => setFormData({ ...formData, founding_year: e.target.value ? parseInt(e.target.value) : undefined })}
                  className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: '#E5E7EB' }}
                  placeholder="2020"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                  Employees
                </label>
                <EmployeeRangeSelect
                  value={formData.employees || ''}
                  onChange={(value) => setFormData({ ...formData, employees: value })}
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="auto_enrich"
                checked={formData.auto_enrich ?? true}
                onChange={(e) => setFormData({ ...formData, auto_enrich: e.target.checked })}
                className="w-5 h-5 rounded"
                style={{ accentColor: COLORS.admin.primary }}
              />
              <label htmlFor="auto_enrich" className="text-sm font-medium" style={{ color: '#1F2937' }}>
                Auto Enrich
              </label>
            </div>

            {/* Action Buttons */}
            <div className="pt-6 space-y-3">
              <button
                type="submit"
                className="w-full py-3 rounded-lg font-semibold text-white transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: COLORS.admin.primary }}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Save changes'}
              </button>
              <button
                type="button"
                onClick={handleDiscard}
                className="w-full py-2 text-sm font-medium hover:underline"
                style={{ color: '#6B7280' }}
                disabled={isSubmitting}
              >
                Discard changes
              </button>
            </div>
          </form>
        </div>
      </div>

      <AddSectorModal
        isOpen={showAddSector}
        onClose={() => setShowAddSector(false)}
        onSubmit={handleCreateSector}
      />
    </>
  );
};
